import React from 'react';
import { Box, Button, Chip, LinearProgress, Theme, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { AddCircleOutline, EditOutlined, RemoveCircleOutline, SwapVert, Sync } from '@material-ui/icons';
import { unpackToDateTime } from '@campfire/hot-date';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { taskFieldTypeLabel } from '../../../../../common/task-field-type-labels';
import { entityTypeHeading, entityTypeIcon } from './enterprise-entity-labels';
import { GET_PENDING_UPDATES } from './enterprise-pending-updates.gql';
import {
  GetPendingUpdates,
  GetPendingUpdates_vm_enterprisePendingUpdates as PendingUpdate,
  GetPendingUpdates_vm_enterprisePendingUpdates_changes as PendingChange,
} from './__generated__/GetPendingUpdates';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    heading: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    containerPanel: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    headingPanel: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    bodyPanel: {
      fontSize: '14px',
      color: '#808080',
    },
    entityHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    entityName: {
      fontWeight: 600,
      fontSize: '15px',
      color: theme.color.grey.neutralBrand900,
    },
    entityMeta: {
      fontSize: '12px',
      color: theme.color.grey.neutral200,
    },
    typeRow: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: '2px',
    },
    typeIcon: {
      fontSize: '15px',
      color: theme.color.grey.neutral200,
      marginRight: '6px',
    },
    changeRow: {
      padding: '12px 0',
      borderTop: `1px solid ${theme.color.grey.border}`,
    },
    changeIcon: {
      fontSize: '16px',
      color: theme.color.grey.neutral200,
      marginRight: '10px',
    },
    changeLabel: {
      fontSize: '13px',
      fontWeight: 600,
      color: theme.color.grey.neutralBrand900,
    },
    itemName: {
      fontSize: '13px',
      color: theme.color.grey.neutral400,
      paddingLeft: '6px',
    },
    typeChip: {
      marginLeft: '8px',
      height: '18px',
      fontSize: '11px',
      color: theme.color.grey.neutral300,
      borderColor: theme.color.grey.border,
    },
    comparison: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      paddingTop: '8px',
      paddingLeft: '26px',
    },
    side: {
      flex: '1 1 220px',
      border: `1px solid ${theme.color.grey.border}`,
      borderRadius: '4px',
      padding: '8px 12px',
      minWidth: 0,
    },
    sideLabel: {
      fontSize: '10px',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.04em',
      color: theme.color.grey.neutral200,
      fontWeight: 600,
    },
    sideValue: {
      fontSize: '13px',
      color: theme.color.grey.neutralBrand900,
      wordBreak: 'break-word' as const,
    },
    emptyValue: {
      fontSize: '13px',
      color: theme.color.grey.neutral200,
      fontStyle: 'italic',
    },
    actions: {
      display: 'flex',
      gap: '8px',
      paddingTop: '8px',
      paddingLeft: '26px',
    },
    squareButton: {
      borderRadius: 4,
      textTransform: 'none' as const,
      fontSize: '13px',
    },
  })
);

const changeIcon = (diffType: string) => {
  if (diffType === 'entity_added') return AddCircleOutline;
  if (diffType === 'entity_removed') return RemoveCircleOutline;
  if (diffType === 'reordered') return SwapVert;
  return EditOutlined;
};

const ComparisonSide = ({ label, value }: { label: string; value: string }) => {
  const classes = useStyles();

  return (
    <Box className={classes.side}>
      <Typography className={classes.sideLabel}>{label}</Typography>
      {value === '' ? (
        <Typography className={classes.emptyValue}>Empty</Typography>
      ) : (
        <Typography className={classes.sideValue}>{value}</Typography>
      )}
    </Box>
  );
};

const ChangeComparison = ({ change }: { change: PendingChange }) => {
  const classes = useStyles();

  if (change.diffType === 'field_changed') {
    return (
      <Box className={classes.comparison}>
        <ComparisonSide label='Your copy' value={change.subCurrentValue} />
        <ComparisonSide label='Suggested' value={change.masterValue} />
      </Box>
    );
  }

  if (change.diffType === 'entity_removed') {
    return (
      <Box className={classes.comparison}>
        <ComparisonSide label='Would be removed from your copy' value={change.itemName} />
      </Box>
    );
  }

  if (change.diffType === 'entity_added') {
    return (
      <Box className={classes.comparison}>
        <ComparisonSide label='Would be added to your copy' value={change.itemName} />
      </Box>
    );
  }

  return null;
};

export const AdminConsolePendingUpdates = () => {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });

  const [resolving, setResolving] = React.useState(false);

  const { data, loading, refetch } = useCampfireQuery<GetPendingUpdates, {}>(GET_PENDING_UPDATES);

  const updates = data?.vm.enterprisePendingUpdates ?? [];

  const resolve = (diffIds: string[], accept: boolean) => {
    setResolving(true);

    campfireMutation
      .run({ url: `${apiUrl}/vm/enterprise/resolve-updates`, method: 'post', data: { diffIds, accept } })
      .then((res: any) => {
        setResolving(false);

        if (!res.ok) {
          setSnackbar({
            variant: 'error',
            open: true,
            message: res?.data?.message || 'Could not resolve the updates',
          });
          return;
        }

        const result = res?.data?.data ?? {};
        const failed = result.failed ?? 0;

        setSnackbar({
          variant: failed > 0 ? 'warning' : 'success',
          open: true,
          message:
            failed > 0
              ? `${result.resolved ?? 0} applied, ${failed} could not be applied`
              : `${result.resolved ?? 0} update${result.resolved === 1 ? '' : 's'} ${accept ? 'accepted' : 'rejected'}`,
        });

        if (refetch) {
          setTimeout(refetch, 0);
        }
      })
      .catch(() => {
        setResolving(false);
        setSnackbar({ variant: 'error', open: true, message: 'Could not resolve the updates' });
      });
  };

  const renderUpdate = (update: PendingUpdate) => {
    const TypeIcon = entityTypeIcon(update.rootEntityType);
    const diffIds = update.changes.map((change) => change.diffId);

    return (
      <Box className={classes.containerPanel} key={`${update.rootEntityType}-${update.rootEntityId}`}>
        <Box className={classes.entityHeader}>
          <Box>
            <Typography className={classes.entityName}>{update.name}</Typography>
            <Box className={classes.typeRow}>
              <TypeIcon className={classes.typeIcon} />
              <Typography className={classes.entityMeta}>
                {`${entityTypeHeading(update.rootEntityType)} · ${update.changeCount} change${
                  update.changeCount === 1 ? '' : 's'
                } · suggested ${unpackToDateTime(update.pushedAt).toFormat('d MMM y')}`}
              </Typography>
            </Box>
          </Box>
          {update.changes.length > 0 && (
            <Box display='flex' style={{ gap: 8 }}>
              <Button
                className={classes.squareButton}
                variant='outlined'
                disabled={resolving}
                onClick={() => resolve(diffIds, false)}
              >
                Reject all
              </Button>
              <Button
                className={classes.squareButton}
                variant='contained'
                color='primary'
                disableElevation
                disabled={resolving}
                onClick={() => resolve(diffIds, true)}
              >
                Accept all
              </Button>
            </Box>
          )}
        </Box>

        <Box paddingTop='12px'>
          {update.changes.map((change) => {
            const Icon = changeIcon(change.diffType);
            const fieldType = taskFieldTypeLabel(change.itemFieldType);

            return (
              <Box className={classes.changeRow} key={change.diffId}>
                <Box display='flex' alignItems='center' flexWrap='wrap'>
                  <Icon className={classes.changeIcon} />
                  <Typography className={classes.changeLabel}>{change.summary}</Typography>
                  {change.itemName !== '' && <Typography className={classes.itemName}>{change.itemName}</Typography>}
                  {fieldType !== '' && (
                    <Chip size='small' variant='outlined' label={fieldType} className={classes.typeChip} />
                  )}
                </Box>

                <ChangeComparison change={change} />

                <Box className={classes.actions}>
                  <Button
                    className={classes.squareButton}
                    size='small'
                    disabled={resolving}
                    onClick={() => resolve([change.diffId], false)}
                  >
                    Reject
                  </Button>
                  <Button
                    className={classes.squareButton}
                    size='small'
                    color='primary'
                    disabled={resolving}
                    onClick={() => resolve([change.diffId], true)}
                  >
                    Accept
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Box>
          <Typography variant='h2' className={classes.headingTitle}>
            Pending Updates
          </Typography>
          <Typography variant='body2'>{'Review changes suggested by your master account'}</Typography>
        </Box>
      </Box>

      {loading && <LinearProgress style={{ marginTop: '1rem' }} />}

      {!loading && updates.length === 0 && (
        <Box className={classes.containerPanel}>
          <Typography className={classes.headingPanel}>
            <Sync style={{ fontSize: '14px', paddingRight: '5px' }} /> No pending updates
          </Typography>
          <Typography variant='body2' className={classes.bodyPanel}>
            {
              'When your master account suggests changes, they will appear here for you to accept or reject individually.'
            }
          </Typography>
        </Box>
      )}

      {!loading && updates.map(renderUpdate)}
    </Box>
  );
};
